import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, throwError, from, of } from 'rxjs';
import { catchError, switchMap, tap, map, filter, take } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { StorageService } from './storage.service';

export interface AuthUser {
  id: number | string;
  name?: string;
  email: string;
  roles?: string[];
  permissions?: string[];
  [key: string]: any;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly TOKEN_KEY = 'access_token';
  private readonly EMAIL_KEY = 'auth_user_email';

  private isAuthenticatedSubject = new BehaviorSubject<boolean>(false);
  public isAuthenticated$ = this.isAuthenticatedSubject.asObservable();

  private currentUserSubject = new BehaviorSubject<AuthUser | null>(null);
  public currentUser$ = this.currentUserSubject.asObservable();

  private sessionRestoredSubject = new BehaviorSubject<boolean>(false);
  public sessionRestored$ = this.sessionRestoredSubject.asObservable();

  private apiUrl = `${environment.urlBaseApi}v1/auth`;

  constructor(
    private router: Router,
    private http: HttpClient,
    private storageService: StorageService
  ) {
    this.restoreSession();
    this.setupStorageListener();
  }

  private setupStorageListener() {
    if (!this.storageService.isNative) {
      window.addEventListener('storage', (event) => {
        if (event.key === this.TOKEN_KEY) {
          if (!event.newValue) {
             // Logout detected in another tab
             this.isAuthenticatedSubject.next(false);
             this.currentUserSubject.next(null);
             this.router.navigate(['/login']);
          } else {
             // Login detected in another tab
             this.isAuthenticatedSubject.next(true);
             this.fetchCurrentUser().subscribe();
          }
        }
      });
    }
  }

  login(email: string, password: string, rememberMe: boolean = false): Observable<AuthUser> {
    const isNative = this.storageService.isNative;
    const effectiveRemember = isNative ? true : rememberMe;
    const body: any = { email, password };
    if (isNative) body.platform = 'mobile';

    return this.http.post<{ access_token: string }>(`${this.apiUrl}/login`, body).pipe(
      switchMap(response => {
        if (response?.access_token) {
          return from(this.setAuthToken(response.access_token, effectiveRemember)).pipe(
            switchMap(() => from(this.storageService.set(this.EMAIL_KEY, email, effectiveRemember))),
            tap(() => this.isAuthenticatedSubject.next(true)),
            map(() => response)
          );
        }
        return of(response);
      }),
      switchMap(() => this.fetchCurrentUser())
    );
  }  

  logout(): void {
    this.clearAuthData().then(() => {
      this.isAuthenticatedSubject.next(false);
      this.currentUserSubject.next(null);
      this.router.navigate(['/login']);
    });
  }

  fetchCurrentUser(): Observable<AuthUser> {
    return this.http.get<AuthUser>(`${this.apiUrl}/me`).pipe(
      tap(user => {
        this.currentUserSubject.next(user);
        if (user?.email) {
          this.storageService.set(this.EMAIL_KEY, user.email);
        }
      }),
      catchError(error => throwError(() => error))
    );
  }

  getCurrentUserSnapshot(): AuthUser | null {
    return this.currentUserSubject.value;
  }

  async getStoredEmail(): Promise<string | null> {
    return this.storageService.get(this.EMAIL_KEY);
  }

  /**
   * Verifica si el usuario está logueado. 
   * Espera a que la sesión se restaure antes de retornar.
   */
  isLoggedIn(): Observable<boolean> {
    return this.sessionRestored$.pipe(
      filter(restored => restored), // Esperar a que sea true
      take(1),
      map(() => this.isAuthenticatedSubject.value)
    );
  }

  async getToken(): Promise<string | null> {
    return this.storageService.get(this.TOKEN_KEY);
  }

  private async restoreSession(): Promise<void> {
    try {
      const token = await this.getToken();
      if (token) {
        this.isAuthenticatedSubject.next(true);
        // Verificar validez del token obteniendo usuario
        this.fetchCurrentUser().subscribe({
          next: () => console.log('Sesión restaurada y validada'),
          error: (err) => {
            console.warn('Sesión inválida o expirada', err);
            this.logout();
          }
        });
      } else {
        this.clearAuthData();
      }
    } catch (error) {
      console.error('Error restaurando sesión', error);
      this.clearAuthData();
    } finally {
      this.sessionRestoredSubject.next(true);
    }
  }

  private async setAuthToken(token: string, remember: boolean = true): Promise<void> {
    await this.storageService.set(this.TOKEN_KEY, token, remember);
  }

  private async clearAuthData(): Promise<void> {
    await this.storageService.remove(this.TOKEN_KEY);
    await this.storageService.remove(this.EMAIL_KEY);
    this.currentUserSubject.next(null);
  }

  // Método para refresh token (placeholder si no hay endpoint)
  refreshToken(): Observable<any> {
    // Si hubiera endpoint: return this.http.post(...)
    // Por ahora retornamos error para forzar logout en 401
    return throwError(() => new Error('Refresh token not implemented'));
  }
}
